import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DismissibleTooltip } from '../common/DismissibleTooltip'
import { useMindmaps } from '../../hooks/useMindmaps'
import { ProfileService } from '../../services/profile/profileService'
import { CreateProjectModal } from '../Dashboard/CreateProjectModal'
import { UpgradePromptModal } from '../UpgradePromptModal'
import { Lock } from 'lucide-react'
import {
  BookIcon,
  ProjectLabel,
  ProjectSelect,
  ProjectSelectorContainer,
  DropdownDivider,
  CreateProjectOption,
  PlusIcon,
  ArchivedChip,
  ProjectOptionLabel,
  ArchivedIcon,
} from './styles/ProjectSelectorStyles'

export function ProjectSelector() {
  const { id: mindmapId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { mindmaps, loading, loadMindmaps } = useMindmaps()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Find current mindmap if we're in the editor
  const currentMindmap = mindmapId ? mindmaps.find((m) => m.id === mindmapId) : null

  // Show dropdown if we're in a mindmap route, even if mindmap isn't loaded yet
  const shouldShowProjectDropdown = !!mindmapId

  // Refetch mindmaps when navigating to a mindmap page to get fresh titles
  useEffect(() => {
    if (mindmapId) {
      void loadMindmaps()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMindmaps is stable, only re-run on mindmapId change
  }, [mindmapId])

  // Update browser tab title with project name
  useEffect(() => {
    if (currentMindmap?.title) {
      document.title = `${currentMindmap.title} - Bandos`
    } else {
      document.title = 'Bandos'
    }
    return () => {
      document.title = 'Bandos'
    }
  }, [currentMindmap?.title])

  const handleCreateProject = async () => {
    const { data } = await ProfileService.getProjectQuota()
    if (data && !data.canCreate && !data.isUnlimited) {
      setShowUpgradeModal(true)
    } else {
      setShowCreateModal(true)
    }
  }

  const handleProjectChange = (projectId: string) => {
    if (projectId === '__create_new__') {
      void handleCreateProject()
    } else {
      void navigate(`/mindmap/${projectId}`)
    }
  }

  if (!shouldShowProjectDropdown) {
    return null
  }

  // Only show value when we have the actual mindmap data (prevents showing raw ID)
  const selectValue = currentMindmap?.id

  return (
    <>
      <ProjectSelectorContainer align="center" gap="small">
        <BookIcon />
        <ProjectLabel>Project:</ProjectLabel>
        <ProjectSelect
          value={selectValue}
          onChange={handleProjectChange}
          loading={loading}
          placeholder="Select a project..."
          showSearch
          filterOption={(input, option) => {
            // Find the mindmap by ID to get its title for filtering
            const mindmap = mindmaps.find((m) => m.id === option?.value)
            if (!mindmap) return false
            return mindmap.title.toLowerCase().includes(input.toLowerCase())
          }}
          options={mindmaps.map((mindmap) => ({
            value: mindmap.id,
            label: mindmap.is_archived ? (
              <ProjectOptionLabel>
                {mindmap.title}
                <ArchivedIcon>
                  <Lock size={12} />
                </ArchivedIcon>
              </ProjectOptionLabel>
            ) : (
              mindmap.title
            ),
          }))}
          notFoundContent="No projects found"
          popupRender={(menu) => (
            <>
              {menu}
              <DropdownDivider />
              <CreateProjectOption
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void handleCreateProject()}
              >
                <PlusIcon />
                Create new project
              </CreateProjectOption>
            </>
          )}
        />
        {currentMindmap?.is_archived && (
          <DismissibleTooltip title="This project is archived. You can view but not edit.">
            <ArchivedChip>
              <Lock size={12} />
              Archived
            </ArchivedChip>
          </DismissibleTooltip>
        )}
      </ProjectSelectorContainer>
      <CreateProjectModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUpgradeClick={() => setShowUpgradeModal(true)}
      />

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName="Unlimited projects"
      />
    </>
  )
}
